/**
 * iter249 — Master Sheets (PO + Invoice) page
 *
 * Replaces the old Configuration Master.  Only 2 tabs:
 *   - PO Masters
 *   - Invoice Masters
 * Each master corresponds to an Excel worksheet.  SA can view /
 * edit / delete items and re-import the workbook.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { useAuth } from '../App';
import { useNavigate } from 'react-router-dom';
import { FileSpreadsheet, Search, ChevronRight, Plus, Trash2, Save, Upload, Download, ArrowLeft, RefreshCw, Package, Receipt } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const currency = (v, code = 'INR') => {
  try { return new Intl.NumberFormat('en-IN', { style: 'currency', currency: code, maximumFractionDigits: 0 }).format(v || 0); }
  catch { return `₹${(v || 0).toLocaleString()}`; }
};

async function api(path, body, token) {
  const res = await fetch(`${API}/api/masters${path}`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, ...body }),
  });
  const j = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(typeof j.detail === 'string' ? j.detail : (j.detail?.message || 'Request failed'));
  return j;
}

/* ── Master list card ── */
function MasterCard({ master, onOpen }) {
  return (
    <Card className="cursor-pointer hover:border-primary transition" onClick={() => onOpen(master)} data-testid={`master-card-${master.worksheet_name.replace(/\s+/g, '-')}`}>
      <CardContent className="pt-4 pb-4 space-y-2">
        <div className="flex items-center justify-between">
          <div className="font-semibold text-sm flex items-center gap-2">
            <FileSpreadsheet className="w-4 h-4 text-primary" />
            {master.worksheet_name}
          </div>
          <ChevronRight className="w-4 h-4 text-muted-foreground" />
        </div>
        <div className="grid grid-cols-3 gap-2 text-xs">
          <div><div className="text-muted-foreground text-[10px]">Items</div><div className="font-semibold">{master.row_count}</div></div>
          <div><div className="text-muted-foreground text-[10px]">Est. Value</div><div className="font-semibold">{currency(master.estimated_value)}</div></div>
          <div><div className="text-muted-foreground text-[10px]">Missing Rate</div><div className={`font-semibold ${master.missing_rate ? 'text-amber-600' : 'text-green-600'}`}>{master.missing_rate}</div></div>
        </div>
      </CardContent>
    </Card>
  );
}

/* ── Master items editor ── */
function MasterEditor({ master: initialMaster, onBack, isSA, token }) {
  const [master, setMaster] = useState(initialMaster);
  const [items, setItems] = useState(initialMaster.items || []);
  const [selected, setSelected] = useState(new Set());
  const [editing, setEditing] = useState(null);
  const [q, setQ] = useState('');

  useEffect(() => { setItems(initialMaster.items || []); setSelected(new Set()); }, [initialMaster]);

  const total = useMemo(() => items.reduce((s, it) =>
    s + (Number(it.quantity || 0) * Number(it.rate || 0)), 0), [items]);
  const missing = useMemo(() => items.filter(it => !it.rate).length, [items]);
  const filtered = useMemo(() => items.filter(it =>
    !q || (it.item || '').toLowerCase().includes(q.toLowerCase())
    || (it.specification || '').toLowerCase().includes(q.toLowerCase())), [items, q]);

  const toggleSel = (id) => setSelected(s => {
    const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n;
  });
  const toggleAll = () => setSelected(s => s.size === filtered.length ? new Set() : new Set(filtered.map(it => it.id)));

  const saveItem = async () => {
    if (!isSA) return toast.error('SA only');
    try {
      await api(`/item/${master.master_type}/${master.id}/save`, { item: editing }, token);
      toast.success('Item saved');
      setEditing(null);
      const r = await api(`/get/${master.master_type}/${master.id}`, {}, token);
      setMaster(r); setItems(r.items || []);
    } catch (e) { toast.error(e.message); }
  };

  const bulkDelete = async () => {
    if (!isSA) return toast.error('SA only');
    if (selected.size === 0) return toast.error('Select at least one item');
    if (!window.confirm(`Delete ${selected.size} item(s) from ${master.worksheet_name}?`)) return;
    try {
      await api(`/items-delete/${master.master_type}/${master.id}`, { item_ids: Array.from(selected) }, token);
      toast.success(`${selected.size} item(s) deleted`);
      setSelected(new Set());
      const r = await api(`/get/${master.master_type}/${master.id}`, {}, token);
      setMaster(r); setItems(r.items || []);
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="space-y-3" data-testid="master-editor">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button size="sm" variant="ghost" onClick={onBack} data-testid="btn-back-to-list"><ArrowLeft className="w-4 h-4 mr-1" />Back</Button>
          <div>
            <div className="font-bold text-lg flex items-center gap-2"><FileSpreadsheet className="w-5 h-5 text-primary" />{master.worksheet_name}</div>
            <div className="text-xs text-muted-foreground">{master.master_type} MASTER · {items.length} items · Est. {currency(total)}{missing > 0 && ` · ${missing} without rate`}</div>
          </div>
        </div>
        <div className="flex gap-2">
          {isSA && <Button size="sm" onClick={() => setEditing({ id: '', sr: items.length + 1, item: '', specification: '', side: '', unit: 'NO.', quantity: 1, rate: null, gst_percent: 0, remarks: '' })} data-testid="btn-add-item"><Plus className="w-3 h-3 mr-1" />Add Item</Button>}
          {isSA && selected.size > 0 && <Button size="sm" variant="destructive" onClick={bulkDelete} data-testid="btn-bulk-delete"><Trash2 className="w-3 h-3 mr-1" />Delete ({selected.size})</Button>}
        </div>
      </div>
      <div className="relative">
        <Search className="absolute left-2 top-2.5 w-3 h-3 text-muted-foreground" />
        <Input className="pl-7 h-8 text-xs" placeholder="Search item / spec..." value={q} onChange={e => setQ(e.target.value)} data-testid="search-items" />
      </div>
      <div className="border rounded overflow-x-auto">
        <table className="w-full text-xs" data-testid="master-items-table">
          <thead className="bg-muted/40">
            <tr className="text-left border-b">
              <th className="p-2 w-8"><input type="checkbox" checked={filtered.length > 0 && selected.size === filtered.length} onChange={toggleAll} /></th>
              <th className="p-2 w-10">Sr</th>
              <th className="p-2">Item / Description</th>
              <th className="p-2 w-32">Side</th>
              <th className="p-2 w-16 text-center">U/M</th>
              <th className="p-2 w-20 text-right">Qty</th>
              <th className="p-2 w-24 text-right">Rate</th>
              <th className="p-2 w-14 text-center">GST%</th>
              <th className="p-2 w-24 text-right">Amount</th>
              <th className="p-2 w-20 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(it => {
              const amt = (Number(it.quantity || 0) * Number(it.rate || 0));
              return (
                <tr key={it.id} className={`border-b ${!it.rate ? 'bg-amber-50' : ''}`} data-testid={`item-row-${it.id}`}>
                  <td className="p-2"><input type="checkbox" checked={selected.has(it.id)} onChange={() => toggleSel(it.id)} /></td>
                  <td className="p-2 text-muted-foreground">{it.sr}</td>
                  <td className="p-2">
                    <div className="font-semibold">{it.item || <em className="text-muted-foreground">(no name)</em>}</div>
                    {it.specification && <div className="text-[10px] text-muted-foreground whitespace-pre-line">{it.specification}</div>}
                  </td>
                  <td className="p-2 text-[10px] text-muted-foreground">{it.side || '—'}</td>
                  <td className="p-2 text-center">{it.unit || '—'}</td>
                  <td className="p-2 text-right">{it.quantity ?? '—'}</td>
                  <td className={`p-2 text-right ${!it.rate ? 'text-amber-700 font-semibold' : ''}`}>{it.rate ? currency(it.rate) : 'RATE REQ'}</td>
                  <td className="p-2 text-center">{it.gst_percent ?? 0}</td>
                  <td className="p-2 text-right font-semibold">{amt > 0 ? currency(amt) : '—'}</td>
                  <td className="p-2 text-right">{isSA && <Button size="sm" variant="ghost" onClick={() => setEditing({ ...it })} data-testid={`btn-edit-item-${it.id}`}><Save className="w-3 h-3" /></Button>}</td>
                </tr>
              );
            })}
            {filtered.length === 0 && <tr><td colSpan={10} className="text-center py-8 text-muted-foreground">No items {q && `matching "${q}"`}</td></tr>}
          </tbody>
          <tfoot>
            <tr className="bg-muted/40 font-semibold">
              <td colSpan={8} className="p-2 text-right">Estimated Total</td>
              <td className="p-2 text-right">{currency(total)}</td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </div>

      <Dialog open={!!editing} onOpenChange={() => setEditing(null)}>
        <DialogContent data-testid="item-editor-modal">
          <DialogHeader><DialogTitle>{editing?.id ? 'Edit' : 'Add'} Item — {master.worksheet_name}</DialogTitle></DialogHeader>
          {editing && (
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="col-span-2"><Label>Item / Description *</Label><Input value={editing.item || ''} onChange={e => setEditing(f => ({ ...f, item: e.target.value }))} data-testid="form-item" /></div>
              <div className="col-span-2"><Label>Specification (optional, multi-line)</Label><Textarea rows={3} value={editing.specification || ''} onChange={e => setEditing(f => ({ ...f, specification: e.target.value }))} data-testid="form-spec" /></div>
              <div><Label>Side / Size / Brand</Label><Input value={editing.side || ''} onChange={e => setEditing(f => ({ ...f, side: e.target.value }))} /></div>
              <div><Label>U/M</Label><Input value={editing.unit || ''} onChange={e => setEditing(f => ({ ...f, unit: e.target.value }))} /></div>
              <div><Label>Qty</Label><Input type="number" value={editing.quantity ?? ''} onChange={e => setEditing(f => ({ ...f, quantity: e.target.value === '' ? null : +e.target.value }))} data-testid="form-qty" /></div>
              <div><Label>Rate (blank = required)</Label><Input type="number" value={editing.rate ?? ''} onChange={e => setEditing(f => ({ ...f, rate: e.target.value === '' ? null : +e.target.value }))} data-testid="form-rate" /></div>
              <div><Label>GST %</Label><Input type="number" value={editing.gst_percent ?? 0} onChange={e => setEditing(f => ({ ...f, gst_percent: +e.target.value }))} /></div>
              <div><Label>Remarks</Label><Input value={editing.remarks || ''} onChange={e => setEditing(f => ({ ...f, remarks: e.target.value }))} /></div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button><Button onClick={saveItem} data-testid="btn-save-item">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ── Import from Excel ── */
function ImportDialog({ open, onClose, token, onDone }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const doPreview = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('token', token); fd.append('file', file);
      const res = await fetch(`${API}/api/masters/import-excel/preview`, { method: 'POST', body: fd });
      const j = await res.json();
      if (!res.ok) throw new Error(j.detail || 'Preview failed');
      setPreview(j);
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  const doConfirm = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('token', token); fd.append('file', file);
      const res = await fetch(`${API}/api/masters/import-excel/confirm`, { method: 'POST', body: fd });
      const j = await res.json();
      if (!res.ok) throw new Error(j.detail || 'Import failed');
      toast.success(`Imported: ${j.po_new + j.po_updated} PO · ${j.invoice_new + j.invoice_updated} Invoice`);
      onDone(); onClose();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="import-modal" className="max-w-2xl">
        <DialogHeader><DialogTitle>Import Master Sheets from Excel</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <input type="file" accept=".xlsx" onChange={e => { setFile(e.target.files[0]); setPreview(null); }} data-testid="import-file" />
          {file && !preview && <Button size="sm" onClick={doPreview} disabled={busy}>Preview</Button>}
          {preview && (
            <div className="border rounded overflow-y-auto max-h-72">
              <table className="w-full text-xs">
                <thead className="bg-muted/40 sticky top-0"><tr className="text-left border-b"><th className="p-2">Worksheet</th><th className="p-2">Detected Type</th><th className="p-2 text-right">Rows</th></tr></thead>
                <tbody>{preview.sheets.map(s => <tr key={s.worksheet_name} className="border-b"><td className="p-2 font-semibold">{s.worksheet_name}</td><td className="p-2"><Badge variant="outline" className="text-[9px]">{s.master_type}</Badge></td><td className="p-2 text-right">{s.row_count}</td></tr>)}</tbody>
              </table>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          {preview && <Button onClick={doConfirm} disabled={busy} data-testid="btn-confirm-import">Confirm Import</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ── Main page ── */
export default function MasterSheets() {
  const { session } = useAuth();
  const nav = useNavigate();
  const token = session?.token;
  const isSA = !!session?.is_super_admin;
  const [poList, setPoList] = useState([]);
  const [invList, setInvList] = useState([]);
  const [selectedMaster, setSelectedMaster] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [q, setQ] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await api('/list', {}, token);
      setPoList(r.po_masters || []); setInvList(r.invoice_masters || []);
    } catch (e) { toast.error(e.message); }
  }, [token]);

  useEffect(() => { if (token) load(); }, [load, token]);

  const openMaster = async (m) => {
    try {
      const r = await api(`/get/${m.master_type}/${m.id}`, {}, token);
      setSelectedMaster(r);
    } catch (e) { toast.error(e.message); }
  };

  const filterList = (list) => list.filter(m => !q || m.worksheet_name.toLowerCase().includes(q.toLowerCase()));

  if (!token) return <div className="p-8">Please sign in.</div>;

  return (
    <div className="p-6 space-y-4" data-testid="master-sheets-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><FileSpreadsheet className="w-6 h-6 text-primary" />Master Sheets</h1>
          <div className="text-xs text-muted-foreground">PO and Invoice masters — one per Excel worksheet.</div>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={load} data-testid="btn-refresh"><RefreshCw className="w-3 h-3 mr-1" />Refresh</Button>
          {isSA && <Button size="sm" onClick={() => setShowImport(true)} data-testid="btn-import"><Upload className="w-3 h-3 mr-1" />Import from Excel</Button>}
          <Button size="sm" variant="ghost" onClick={() => nav('/')}>Home</Button>
        </div>
      </div>

      {selectedMaster ? (
        <MasterEditor master={selectedMaster} onBack={() => { setSelectedMaster(null); load(); }} isSA={isSA} token={token} />
      ) : (
        <>
          <div className="relative max-w-md">
            <Search className="absolute left-2 top-2.5 w-3 h-3 text-muted-foreground" />
            <Input className="pl-7 h-8 text-xs" placeholder="Search master..." value={q} onChange={e => setQ(e.target.value)} data-testid="search-master" />
          </div>
          <Tabs defaultValue="po">
            <TabsList>
              <TabsTrigger value="po" data-testid="tab-po-masters"><Package className="w-3 h-3 mr-1" />PO Masters ({poList.length})</TabsTrigger>
              <TabsTrigger value="inv" data-testid="tab-inv-masters"><Receipt className="w-3 h-3 mr-1" />Invoice Masters ({invList.length})</TabsTrigger>
            </TabsList>
            <TabsContent value="po">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="po-master-grid">
                {filterList(poList).map(m => <MasterCard key={m.id} master={m} onOpen={openMaster} />)}
                {filterList(poList).length === 0 && <div className="col-span-full text-center py-8 text-muted-foreground">No PO masters {q && `matching "${q}"`}</div>}
              </div>
            </TabsContent>
            <TabsContent value="inv">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="inv-master-grid">
                {filterList(invList).map(m => <MasterCard key={m.id} master={m} onOpen={openMaster} />)}
                {filterList(invList).length === 0 && <div className="col-span-full text-center py-8 text-muted-foreground">No Invoice masters {q && `matching "${q}"`}</div>}
              </div>
            </TabsContent>
          </Tabs>
        </>
      )}

      <ImportDialog open={showImport} onClose={() => setShowImport(false)} token={token} onDone={load} />
    </div>
  );
}
